import { useCallback, useEffect, useRef, useState } from "react";

// "Delete, with Undo." request(id) hides the item at once; it is only really deleted (onCommit(id)) once the
// Undo window passes. A second delete makes the first final straight away, and leaving the screen does too.
// onCommit may return false (or a promise of false) if it failed, which puts the item back.
export default function useUndoDelete(onCommit, ms = 5000) {
  const [hidden, setHidden] = useState([]);
  const [pendingId, setPendingId] = useState(null);
  const timerRef = useRef(null);
  const pendingRef = useRef(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const commit = useCallback((id) => {
    Promise.resolve(commitRef.current(id)).then((ok) => {
      if (ok === false) setHidden((h) => h.filter((x) => x !== id));
    });
  }, []);

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    if (pendingRef.current !== null) {
      commit(pendingRef.current);
      pendingRef.current = null;
    }
    setPendingId(null);
  }, [commit]);

  const request = useCallback(
    (id) => {
      flush();
      pendingRef.current = id;
      setHidden((h) => [...h, id]);
      setPendingId(id);
      timerRef.current = setTimeout(() => {
        if (pendingRef.current !== null) commit(pendingRef.current);
        pendingRef.current = null;
        setPendingId(null);
      }, ms);
    },
    [flush, commit, ms]
  );

  const undo = useCallback(() => {
    clearTimeout(timerRef.current);
    const id = pendingRef.current;
    pendingRef.current = null;
    if (id !== null) setHidden((h) => h.filter((x) => x !== id));
    setPendingId(null);
  }, []);

  // keep the hidden list tidy once the items are really gone
  const forget = useCallback((existingIds) => setHidden((h) => h.filter((id) => existingIds.includes(id))), []);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      if (pendingRef.current !== null) commit(pendingRef.current);
    },
    [commit]
  );

  return { hidden, pendingId, request, undo, forget };
}
